import { InfoCircleFilled, LinkOutlined } from '@ant-design/icons';
import { Button, Input, Space, Switch, Tooltip, Typography } from 'antd';
import { FieldData } from 'rc-field-form/es/interface';
import { Trans, useTranslation } from 'react-i18next';

import Card from '../../../Card';
import useAPIQuery from '../../../api/useAPIQuery';
import Config from '../../../app/config';
import Form from '../../../components/Form';
import useTypedParams from '../../../hooks/useTypedParams';
import { JobPostingId } from '../../../types';
import { handleJobPostingDescriptionLengthValidator } from '../../../validators/jobPostingDescriptionValidator';

interface UnlistedFormProps {
  loading?: boolean;
  disabled?: boolean;
}

const UnlistedForm = ({ loading, disabled }: UnlistedFormProps) => {
  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });

  const { t } = useTranslation();

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const vacancyPortalLink = jobPosting.data?.unlisted_external_publication
    ? `${Config.REACT_APP_CAREER_PAGE_URL}${tenant}/${id}`
    : ``;

  const externalPublicationValue = Form.useWatch<boolean>('externalPublication');

  return (
    <Form.Item
      shouldUpdate={(prev, next) =>
        prev.externalPublication !== next.externalPublication ||
        prev.unlistedExternalPublication !== next.unlistedExternalPublication
      }
    >
      {(form) => (
        <Card
          title={
            <Typography.Text strong>
              <Trans>Vaga Não Listada</Trans>
            </Typography.Text>
          }
          extra={[
            <Tooltip
              title={t(
                'A vaga poderá ser acessada somente por pessoas que possuam o link e não será publicada na página de carreiras.'
              )}
            >
              <InfoCircleFilled />
            </Tooltip>,
          ]}
        >
          <Space direction="vertical">
            <Form.Row align="middle">
              <Tooltip
                title={
                  externalPublicationValue
                    ? t(
                        'A vaga não pode ser divulgada na página de carreiras e como não listada ao mesmo tempo'
                      )
                    : null
                }
              >
                <Form.Col>
                  <Form.Item name="unlistedExternalPublication" valuePropName="checked" noStyle>
                    <Switch
                      defaultChecked={jobPosting.data?.unlisted_external_publication}
                      loading={loading}
                      disabled={disabled}
                      onClick={async (checked) => {
                        form.setFieldsValue({
                          unlistedExternalPublication: checked,
                          externalPublication: false,
                          linkedinExternalPublication: false,
                          netvagasExternalPublication: false,
                          googleForJobsExternalPublication: false,
                          careerjetExternalPublication: false,
                          joobleExternalPublication: false,
                        });
                        try {
                          if (checked) {
                            await form.validateFields(['description', 'country']);
                          }
                          await handleJobPostingDescriptionLengthValidator(
                            undefined,
                            jobPosting.data?.description
                          );
                          form.submit();
                        } catch (err) {
                          form.setFieldsValue({
                            unlistedExternalPublication: false,
                          });
                          if (err && typeof err === 'object' && 'errorFields' in err) {
                            form.setFields(err.errorFields as FieldData[]);
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </Form.Col>
              </Tooltip>
              <Form.Col flex={1}>
                <Form.Item noStyle shouldUpdate>
                  <Input value={vacancyPortalLink} readOnly disabled suffix={<LinkOutlined />} />
                </Form.Item>
              </Form.Col>
              <Form.Col>
                <Tooltip
                  trigger="click"
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  title={
                    <>
                      <Typography.Text style={{ color: 'white' }}>
                        <Trans>Link copiado!</Trans>
                      </Typography.Text>
                    </>
                  }
                >
                  <Button
                    disabled={Boolean(!vacancyPortalLink)}
                    onClick={async () => {
                      await navigator.clipboard.writeText(vacancyPortalLink);
                    }}
                  >
                    <Trans>Copiar link</Trans>
                  </Button>
                </Tooltip>
              </Form.Col>
            </Form.Row>
          </Space>
        </Card>
      )}
    </Form.Item>
  );
};

export default UnlistedForm;
